import { useState, useEffect } from "react"
import { ChevronLeft, ChevronDown, ChevronUp, Plus, Pencil, Trash2, X } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Dialog, DialogContent, DialogClose } from "../../../components/ui/dialog"
import { Card } from "../../../components/ui/card"
import { getAllCategories, createCategory, updateCategory, deleteCategory } from "../../../services/api"
import { toast, ToastContainer } from "react-toastify" // Import toast library
import "react-toastify/dist/ReactToastify.css" // Toast styles

interface ProductCategory {
  id: string
  name: string
  description: string
}

interface ProductCategoriesProps {
  onBack: () => void
}

export function ProductCategories({ onBack }: ProductCategoriesProps) {
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null)
  const [categoryName, setCategoryName] = useState("")
  const [categoryDescription, setCategoryDescription] = useState("")
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      const response = await getAllCategories() as any
      console.log("Fetched categories:", response.data) // Debugging
      setCategories(response.data.data) // Adjust based on API response structure
    } catch (error) {
      console.error("Failed to fetch categories:", error)
      toast.error("Failed to fetch categories. Please try again.") // Toast for error
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenModal = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category)
      setCategoryName(category.name || "") // Ensure default value
      setCategoryDescription(category.description || "") // Ensure default value
    } else {
      setEditingCategory(null)
      setCategoryName("")
      setCategoryDescription("")
    }
    setIsModalOpen(true)
  }

  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      toast.error("Category name is required.") // Toast for validation error
      return
    }

    try {
      if (editingCategory) {
        // Update existing category
        const response = await updateCategory(editingCategory.id, {
          name: categoryName.trim(),
          description: categoryDescription.trim(),
        }) as any
        console.log("Updated category:", response.data) // Debugging
        const updatedCategory = response.data.data // Adjust based on API response structure
        setCategories(categories.map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat)))
        toast.success("Category updated successfully!") // Toast for success
      } else {
        // Add new category
        const response = await createCategory({
          name: categoryName.trim(),
          description: categoryDescription.trim(),
        }) as any
        console.log("Created category:", response.data) // Debugging
        const newCategory = response.data.data // Adjust based on API response structure
        setCategories([...categories, newCategory])
        toast.success("Category added successfully!") // Toast for success
      }
    } catch (error) {
      console.error("Failed to save category:", error)
      toast.error("Failed to save category. Please try again.") // Toast for error
    } finally {
      resetForm()
      setIsModalOpen(false)
    }
  }

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteCategory(id)
      setCategories(categories.filter((cat) => cat.id !== id))
      toast.success("Category deleted successfully!") // Toast for success
    } catch (error) {
      console.error("Failed to delete category:", error)
      toast.error("Failed to delete category. Please try again.") // Toast for error
    }
  }

  const resetForm = () => {
    setCategoryName("")
    setCategoryDescription("")
    setEditingCategory(null)
  }

  const toggleCategory = (id: string) => {
    setExpandedCategory(expandedCategory === id ? null : id)
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <>
      <ToastContainer />
      <div className="flex items-center justify-between mb-6">
        <a
          href="/inventory"
          className="flex items-center text-[#B87775] hover:underline"
          onClick={(e) => {
            e.preventDefault()
            onBack()
          }}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          INVENTORY
        </a>
        <Button className="bg-[#B87775] hover:bg-[#B87775]/90 text-white" onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          ADD CATEGORY
        </Button>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-medium mb-6">Product Category</h2>

        {categories.length === 0 ? (
          <div className="text-center text-muted-foreground py-6">
            <p>No product categories found.</p>
            <p className="mt-2">Click the "Add Category" button to create one.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((category) => (
              <div key={category.id} className="border rounded-md overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleCategory(category.id)}
                >
                  <h3 className="text-lg font-medium">{category.name}</h3>
                  {expandedCategory === category.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </div>

                {expandedCategory === category.id && (
                  <div className="p-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-1">Description</p>
                    <p className="mb-4 font-normal">{category.description}</p>

                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#B87775] bg-[#B87775]/10 hover:text-[#B87775] hover:bg-[#B87775]/20 px-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenModal(category)
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 bg-red-50 hover:text-red-500 hover:bg-red-100 px-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteCategory(category.id)
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-medium">
              {editingCategory ? "Edit Product Category" : "Add Product Category"}
            </h2>
          </div>

          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-6 w-6 text-[#B87775]" />
            <span className="sr-only">Close</span>
          </DialogClose>

          <div className="space-y-6">
            <div>
              <label htmlFor="categoryName" className="block text-sm font-medium mb-2">
                Category Title <span className="text-red-500">*</span>
              </label>
              <Input
                id="categoryName"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="eg for the eyes"
                className="w-full"
                required
              />
            </div>

            <div>
              <label htmlFor="categoryDescription" className="block text-sm font-medium mb-2">
                Category Description
              </label>
              <Textarea
                id="categoryDescription"
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Max 50 words"
                className="w-full min-h-[180px] resize-none"
              />
            </div>

            <div className="flex justify-center gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  resetForm()
                  setIsModalOpen(false)
                }}
                className="uppercase border-[#B87775] text-[#B87775] px-8"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!categoryName.trim()}
                className="bg-[#B87775] hover:bg-[#B87775]/90 text-white uppercase px-8"
              >
                {editingCategory ? "Save" : "Upload"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}